import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAnalysisStore, AssessmentHistoryItem } from '../../store/analysisStore';
import Card from '../common/Card';
import { Box, Typography, Stack, Tabs, Tab, useMediaQuery, useTheme } from '@mui/material';
import { computeScores } from './computeScores';

const SCORE_KEYS = ['final', 'quality', 'veracity_final', 'valence', 'manipulation'] as const;

const SCORE_META: Record<string, { label: string; color: string; max: number }> = {
  final: { label: 'Trust Index', color: '#1a1a18', max: 100 },
  quality: { label: 'Quality', color: '#0F6E56', max: 100 },
  veracity_final: { label: 'Veracity', color: '#185FA5', max: 100 },
  valence: { label: 'Valence', color: '#E06820', max: 100 },
  manipulation: { label: 'Manipulation', color: '#A32D2D', max: 100 },
};

interface Props {
  data?: AssessmentHistoryItem[];
}

export default function AssessmentChart({ data }: Props) {
  const { assessmentHistory: globalHistory } = useAnalysisStore();
  const historyToUse = data || globalHistory;
  const [selectedMetric, setSelectedMetric] = useState<string>('final');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartHeight = isMobile ? 260 : 400;

  const chartData = [...historyToUse]
    .reverse()
    .map((item, index) => {
      const c = computeScores(item.result);
      return {
        index: index + 1,
        title: item.result.article.title?.substring(0, 30) || `#${index + 1}`,
        final: c.finalScore,
        quality: item.result.is_opinion ? null : c.qualityTotal,
        veracity_final: c.veracityFinal,
        valence: item.result.is_opinion ? null : c.valenceTotal,
        manipulation: c.manipulationTotal,
      };
    });

  if (chartData.length === 0) {
    return (
      <Card title="Assessment Trends">
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" textAlign="center">
          <Typography color="text.secondary">
            Run assessments to see trends here.
          </Typography>
        </Box>
      </Card>
    );
  }

  // Stats for selected metric
  const values = chartData
    .map(d => d[selectedMetric as keyof typeof d])
    .filter((v): v is number => v != null);

  const stats = values.length > 0 ? {
    min: Math.min(...values),
    max: Math.max(...values),
    mean: values.reduce((a, b) => a + b, 0) / values.length,
    stdDev: Math.sqrt(values.reduce((sum, v) => sum + Math.pow(v - values.reduce((a, b) => a + b, 0) / values.length, 2), 0) / values.length),
  } : null;

  return (
    <Card
      title="Assessment Trends"
      sx={{ height: '100%' }}
      footer={
        <Box sx={{ width: '100%' }}>
          <Tabs
            value={selectedMetric}
            onChange={(_, v) => setSelectedMetric(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 44, sm: 36 },
              '& .MuiTab-root': {
                minHeight: { xs: 44, sm: 36 },
                padding: { xs: '8px 14px', sm: '6px 12px' },
                fontSize: { xs: '0.8rem', sm: '0.75rem' },
                textTransform: 'none',
              },
              borderBottom: 1,
              borderColor: 'divider',
              mb: 1.5,
            }}
          >
            {SCORE_KEYS.map((key) => (
              <Tab
                key={key}
                label={SCORE_META[key].label}
                value={key}
                sx={{
                  color: SCORE_META[key].color,
                  '&.Mui-selected': { color: SCORE_META[key].color, fontWeight: 'bold' },
                }}
              />
            ))}
          </Tabs>

          {stats && (
            <Stack spacing={1} width="100%">
              {[
                ['Minimum', stats.min],
                ['Maximum', stats.max],
                ['Mean', stats.mean],
                ['Std. Dev', stats.stdDev],
                ['Assessments', values.length],
              ].map(([label, value]) => (
                <Box key={label as string} display="flex" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">{label as string}:</Typography>
                  <Typography variant="caption" fontWeight="bold">
                    {typeof value === 'number' ? (label === 'Assessments' ? value : value.toFixed(2)) : value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      }
    >
      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="index" tickFormatter={(v) => `#${v}`} />
          <YAxis domain={[0, SCORE_META[selectedMetric]?.max || 100]} />
          <Tooltip
            formatter={(value: number | undefined, name: string | undefined) => [
              `${SCORE_META[name || '']?.label || name}: ${value ?? '—'}`,
              '',
            ]}
            labelFormatter={(label) => `Assessment #${label}`}
          />
          <Legend />
          {SCORE_KEYS.map((key) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={SCORE_META[key].color}
              strokeWidth={key === selectedMetric ? 3 : 1.5}
              strokeOpacity={key === selectedMetric ? 1 : 0.3}
              dot={{ r: key === selectedMetric ? 4 : 2 }}
              activeDot={{ r: 5 }}
              name={key}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}
